import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { DateRangePicker } from '@mui/lab'
import { Button, CircularProgress, IconButton, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import VouchersAPI from 'api/faber/VouchersAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import format from 'date-fns/format'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { VoucherTableRows } from 'types/api/faber/vouchers.type'

import {
  getFilterOptions,
  IFilterField
} from '../../../../../common/AbstractDataGridToolbar/CustomDataGridFilter'

import FaberConfirmVoucherDeleteDialog from './FaberConfirmVoucherDeleteDialog'

export const StyleIconButton = styled(IconButton)({
  borderRadius: '8px',
  color: circlesOrbitColorPalette.primary.main,
  padding: '0px 16px 0px 0px',
  '&:hover': {
    backgroundColor: circlesOrbitColorPalette.neutrals[100]
  }
})

interface FaberVouchersTableProps {
  numVouchers: number | undefined
}

export default function FaberVouchersTable(props: FaberVouchersTableProps) {
  const [amtChecked, setAmtChecked] = useState<string[]>([])
  const [redeemedChecked, setRedeemedChecked] = useState<string[]>([])
  const [rows, setRows] = useState<VoucherTableRows[]>([])
  const [filteredRows, setFilteredRows] = useState<VoucherTableRows[]>([])
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dgLoading, setDgLoading] = useState<boolean>(true)

  const [startDate, setStartDate] = useState<Date>(changeMonth(new Date(), -3))
  const [endDate, setEndDate] = useState<Date>(changeMonth(new Date(), +3))

  const [page] = useState(1)
  const [pageSize] = useState(100)

  const [confirmDeleteVouchers, setConfirmDeleteVouchers] = useState(false)
  const faberToken = OrbitSession.get('faberToken')
  const navigate = useNavigate()
  const location = useLocation()

  const { numVouchers } = props

  const { mutate: deleteVouchers } = useMutation(VouchersAPI.deleteVouchers, {
    onSuccess: (data) => {
      toast.success(data.data.message, {
        hideProgressBar: false,
        autoClose: 3000
      })
      setInterval(() => {
        navigate(0)
      }, 3000)
    },
    onError: () => {
      toast.error('Failed to delete vouchers')
    }
  })

  function UploadBtn() {
    return (
      <Button
        variant='contained'
        size='small'
        onClick={() => navigate('upload')}
        sx={{ minWidth: '220px' }}
      >
        Upload Voucher CSV
      </Button>
    )
  }

  function DeleteVouchersBtn() {
    return (
      <Button
        variant='contained'
        size='small'
        onClick={handleDeleteVouchers}
        sx={{ minWidth: '220px' }}
      >
        Delete Vouchers
      </Button>
    )
  }

  function VouchersButtons() {
    return (
      <Stack direction='row'>
        <UploadBtn />
        <DeleteVouchersBtn />
      </Stack>
    )
  }

  const handleDeleteVouchers = () => {
    setConfirmDeleteVouchers(true)
  }

  const handleCloseDeleteDialog = () => {
    setConfirmDeleteVouchers(false)
  }

  const handleConfirmDelete = () => {
    deleteVouchers(faberToken)
    handleCloseDeleteDialog()
  }

  const role: string = OrbitSession.get('role')

  const dataGridTypographySX = {
    color: 'black',
    fontSize: '12px'
  }

  const columns: GridColDef[] = [
    {
      field: 'redeemedAt',
      headerName: 'Redemption Date',
      minWidth: 100,
      renderCell: (params: { row: VoucherTableRows }) => {
        if (params.row.redeemedAt) {
          const date = new Date(`${params.row.redeemedAt}`)

          return (
            <Typography sx={dataGridTypographySX}>
              {format(date, 'dd MMM yyyy HH:mm')}
            </Typography>
          )
        }
        return ''
      },
      flex: 1
    },
    {
      field: 'userEmail',
      headerName: 'User Email',
      minWidth: 150,
      flex: 2,
      renderCell: (params: { row: VoucherTableRows }) => {
        return (
          <AbstractDataGridTooltip>
            {params.row.userEmail}
          </AbstractDataGridTooltip>
        )
      }
    },
    { field: 'msisdn', headerName: 'MSISDN', minWidth: 100, flex: 1 },
    { field: 'value', headerName: 'Amt', minWidth: 50, flex: 1 },
    {
      field: 'expiryAt',
      headerName: 'Voucher Lapsed',
      minWidth: 100,
      renderCell: (params: { row: VoucherTableRows }) => {
        const date = new Date(`${params.row.expiryAt}`)

        return (
          <Typography sx={dataGridTypographySX}>
            {format(date, 'dd MMM yyyy')}
          </Typography>
        )
      },
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Dispatched',
      minWidth: 100,
      renderCell: (params: { row: VoucherTableRows }) => {
        if (params.row.status === 'REDEEMED') {
          return <Typography sx={dataGridTypographySX}>ACTIVATED</Typography>
        } else if (params.row.status === 'AVAILABLE') {
          return (
            <Typography sx={dataGridTypographySX}>NOT ACTIVATED</Typography>
          )
        }
      },
      flex: 1
    },
    {
      field: 'code',
      headerName: 'Voucher Code ID',
      minWidth: 150,
      flex: 1.5,
      renderCell: (params: { row: VoucherTableRows }) => {
        return (
          <AbstractDataGridTooltip>{params.row.code}</AbstractDataGridTooltip>
        )
      }
    }
  ]

  const queryOptions = React.useMemo(
    () => ({
      page,
      pageSize
    }),
    [page, pageSize]
  )

  const { data: getAllVouchers, refetch: refetchAllVouchers } = useQuery(
    ['getAllVouchers', [queryOptions]],
    async () => {
      return VouchersAPI.getAllVouchers(faberToken, page)
    },
    {
      onError: () => {
        toast.error('Error retrieving vouchers')
        setRows([])
        setIsLoading(false)
        setDgLoading(false)
      }
    }
  )

  useEffect(() => {
    if (getAllVouchers) {
      const { data: allVouchers } = getAllVouchers.data
      if (allVouchers && allVouchers.length && allVouchers.length > 0) {
        const getVouchers: VoucherTableRows[] = allVouchers.map(
          ({
            code,
            value,
            status,
            redeemedAt,
            userEmail,
            expiryAt,
            CreatedAt,
            msisdn
          }) => {
            return {
              createdAt: CreatedAt,
              code,
              value,
              status,
              redeemedAt,
              expiryAt,
              userEmail,
              msisdn
            }
          }
        )
        setIsLoading(false)
        setDgLoading(false)
        setRows(
          // TODO: FILTER OUT DELETED ON THE API SIDE
          getVouchers.filter((row) => {
            return row.status !== 'DELETED'
          })
        )
      } else {
        setRows([])
        setIsLoading(false)
      }
    }
  }, [getAllVouchers, page, pageSize])

  useEffect(() => {
    refetchAllVouchers()
  }, [location.key])

  const filterFields = useMemo(() => {
    let tempFilterFields: IFilterField[] = []
    if (!isLoading) {
      tempFilterFields = [
        {
          values: getFilterOptions(rows.map((row) => row.value)),
          checked: amtChecked,
          setChecked: setAmtChecked,
          name: 'Voucher Amt',
          index: 'value'
        },
        {
          values: getFilterOptions(rows.map((row) => row.status)),
          checked: redeemedChecked,
          setChecked: setRedeemedChecked,
          name: 'Status',
          index: 'status'
        }
      ]
    }
    return tempFilterFields
  }, [rows, amtChecked, redeemedChecked, filteredRows])

  return !isLoading ? (
    <>
      <MUIDataGrid
        getRowId={(row) => row.code}
        tableHeight='725px'
        columns={columns}
        rows={filterActive ? filteredRows : rows}
        /* TEMPORARILY DISABLE SERVER-SIDE PAGINATION FOR DEMO */
        // paginationMode='server'
        // rowCount={1500}
        // pageSize={pageSize}
        // onPageChange={(newPage) => setPage(newPage + 1)}
        components={{ Toolbar: Toolbar }}
        componentsProps={{
          toolbar: {
            filterProps: {
              filterFields,
              filterActive,
              setFilterActive,
              rows,
              setFilteredRows
            },
            dateRangePicker: (
              <DateRangePicker
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            ),
            buttons: role !== 'opsviewer' ? <VouchersButtons /> : null
          }
        }}
        loading={dgLoading}
      />
      <FaberConfirmVoucherDeleteDialog
        open={confirmDeleteVouchers}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        numVouchers={numVouchers ? numVouchers : 0}
      />
    </>
  ) : (
    <CircularProgress />
  )
}
